import { Injectable } from '@angular/core';
import { EcgExamination, VisitExamination } from '@se/models';
import { ExaminationUtilitiesService } from '@se/platform';
import { every, find, isDate, isEmpty } from 'lodash-es';

@Injectable()
export class MergeExamDifferenceChecker {
    private _source: EcgExamination | VisitExamination | null = null;
    get source() {
        return this._source;
    }

    private _target: EcgExamination | VisitExamination | null = null;
    get target() {
        return this._target;
    }

    constructor(private examService: ExaminationUtilitiesService) {}

    init<T extends EcgExamination | VisitExamination>(items: T[]) {
        const [source, target] = this.findSourceAndTarget(items);

        this._source = source ?? null;
        this._target = target ?? null;
    }

    clear() {
        this._source = null;
        this._target = null;
    }

    // #region Operations

    canMerge(items: (EcgExamination | VisitExamination)[]) {
        const [source, target] = this.findSourceAndTarget(items);

        if (!source || !target) {
            return false;
        }

        if (
            !this.examService.verifyLockInfo(source) ||
            !this.examService.verifyLockInfo(target) ||
            source.isMerged ||
            target.isMerged
        ) {
            return false;
        }

        return true;
    }

    canUndoMerge(items: (EcgExamination | VisitExamination)[]) {
        return (
            !isEmpty(items) &&
            // Ogni item dev'essere unlockabile, non definitivo ed oggetto di merge.
            every(items, (item) => {
                return (
                    this.examService.verifyLockInfo(item) &&
                    !item.isApproved &&
                    !item.isDigitallySigned &&
                    item.isMerged
                );
            })
        );
    }

    isSource(item: EcgExamination | VisitExamination): boolean {
        return (
            item &&
            item.patient &&
            item.patient.isLocalPatient &&
            // Stato AE o AER
            !item.isApproved &&
            item.isExecuted
        );
    }

    isTarget(item: EcgExamination | VisitExamination) {
        return (
            item &&
            item.patient &&
            !item.patient.isLocalPatient &&
            // Stato P o A o PA
            !item.isExecuted
        );
    }

    // #endregion

    // #region Differences

    isFirstNameDifferent() {
        if (this.isDifferent()) {
            return true;
        }

        return this.source?.patient.firstName !== this.target?.patient.firstName;
    }

    isLastNameDifferent() {
        if (this.isDifferent()) {
            return true;
        }

        return this.source?.patient.lastName !== this.target?.patient.lastName;
    }

    isSexDifferent() {
        if (this.isDifferent()) {
            return true;
        }

        return this.source?.patient.sex !== this.target?.patient.sex;
    }

    isBirthDateDifferent() {
        if (this.isDifferent()) {
            return true;
        }
        if (isDate(this.source?.patient.birthDate) && isDate(this.target?.patient.birthDate)) {
            return (
                this.source?.patient.birthDate.toLocaleDateString() !==
                this.target?.patient.birthDate.toLocaleDateString()
            );
        }
        if (Date.parse(this.source?.patient.birthDate) && Date.parse(this.target?.patient.birthDate)) {
            // sono date in stringhe
            const sourceBirthDate = new Date(this.source?.patient.birthDate);
            const targetBirthDate = new Date(this.target?.patient.birthDate);

            return sourceBirthDate.toLocaleDateString() !== targetBirthDate.toLocaleDateString();
        }
    }

    isDateDifferent() {
        if (this.isDifferent()) {
            return true;
        }

        if (isDate(this.source?.examinationDate) && isDate(this.target?.examinationDate)) {
            return this.source?.examinationDate.getTime() === this.target?.examinationDate.getTime();
        }

        return this.source?.examinationDate !== this.target?.examinationDate;
    }

    isExamNumberDifferent() {
        if (this.isDifferent()) {
            return true;
        }

        return this.source?.examNumber !== this.target?.examNumber;
    }

    isDescriptionDifferent() {
        if (this.isDifferent()) {
            return true;
        }

        return this.source?.exam.description !== this.target?.exam.description;
    }

    // #endregion

    // #region Private Methods

    private findSourceAndTarget(
        items: (EcgExamination | VisitExamination)[],
    ): [EcgExamination | VisitExamination, EcgExamination | VisitExamination] | [] {
        if (!items || items.length !== 2) {
            return [];
        }

        const source = find(items, this.isSource);
        const destination = find(items, this.isTarget);
        if (!source || !destination) return [];
        return [source, destination];
    }

    private isDifferent() {
        if ((!this.source && this.target) || (this.source && !this.target) || (!this.source && !this.target))
            return true;
    }

    // #endregion
}
